import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  createWorkspaceUser,
  fetchWorkspaceUser,
  fetchWorkspaceUsers,
  resendWorkspaceUserInvite,
  updateWorkspaceUser,
} from '../../api/workspaceUsers';
import { StatusBanner, ConfirmDialog } from '../../components/ui';
import { adminNavItems } from '../admin/adminNav';
import {
  ASSIGN_USERS_NEW_PATH,
  ASSIGN_USERS_PATH,
  EMPTY_USER_FORM,
  StatCard,
  UserTable,
  assignUserEditPath,
  moduleLabel,
} from './assignUserShared';

function AssignUsersPage({ token, user, mode = 'list' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();
  const isForm = mode === 'create' || mode === 'edit';

  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 });
  const [availableModules, setAvailableModules] = useState(
    adminNavItems.map((item) => item.id)
  );
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState({ type: 'idle', message: '' });
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState(EMPTY_USER_FORM);
  const [formReady, setFormReady] = useState(mode === 'create');
  const [pendingToggle, setPendingToggle] = useState(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setStatus({ type: 'idle', message: '' });
    try {
      const payload = await fetchWorkspaceUsers(token, { status: 'all' });
      setUsers(payload.users || []);
      setStats(payload.stats || { total: 0, active: 0, inactive: 0 });
      if (Array.isArray(payload.availableModules) && payload.availableModules.length) {
        setAvailableModules(payload.availableModules);
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Unable to load users.',
      });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (user?.role !== 'admin') {
      return;
    }
    if (!isForm) {
      loadUsers();
    }
  }, [user?.role, isForm, loadUsers]);

  useEffect(() => {
    const flash = location.state?.flash;
    if (!flash || isForm) {
      return;
    }
    setStatus({ type: 'success', message: flash });
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, isForm, navigate]);

  useEffect(() => {
    if (user?.role !== 'admin' || mode !== 'edit' || !userId) {
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setFormReady(false);
      setLoading(true);
      setStatus({ type: 'idle', message: '' });
      try {
        const payload = await fetchWorkspaceUser(token, userId);
        if (cancelled) return;
        const next = payload.user;
        setEditingUser(next);
        setForm({
          name: next.name || '',
          email: next.email || '',
          password: '',
          phone: next.phone || '',
          modules: Array.isArray(next.modules) ? [...next.modules] : ['home'],
          isActive: next.isActive !== false,
        });
        setFormReady(true);
      } catch (error) {
        if (cancelled) return;
        setStatus({
          type: 'error',
          message: error.message || 'Unable to load user.',
        });
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.role, mode, userId, token]);

  useEffect(() => {
    if (mode === 'create') {
      setEditingUser(null);
      setForm(EMPTY_USER_FORM);
      setFormReady(true);
      setLoading(false);
    }
  }, [mode]);

  const filteredUsers = useMemo(() => {
    if (statusFilter === 'active') {
      return users.filter((entry) => entry.isActive);
    }
    if (statusFilter === 'inactive') {
      return users.filter((entry) => !entry.isActive);
    }
    return users;
  }, [users, statusFilter]);

  if (user?.role !== 'admin') {
    return <Navigate to="/admin/dashboard" replace />;
  }

  const toggleModule = (moduleId) => {
    setForm((current) => {
      const has = current.modules.includes(moduleId);
      return {
        ...current,
        modules: has
          ? current.modules.filter((id) => id !== moduleId)
          : [...current.modules, moduleId],
      };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setStatus({ type: 'idle', message: '' });

    try {
      if (!form.modules.length) {
        throw new Error('Assign at least one section to this user.');
      }

      if (mode === 'edit' && editingUser) {
        const payload = {
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          phone: form.phone.trim() || null,
          modules: form.modules,
          isActive: form.isActive,
        };
        if (form.password.trim().length >= 8) {
          payload.password = form.password.trim();
        }
        await updateWorkspaceUser(token, editingUser.id, payload);
        navigate(ASSIGN_USERS_PATH, {
          replace: true,
          state: { flash: 'User updated.' },
        });
        return;
      }

      const created = await createWorkspaceUser(token, {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || null,
        modules: form.modules,
        isActive: form.isActive,
      });

      const inviteSent = created?.invite?.sent === true;
      const inviteError = created?.invite?.error;
      const flash = inviteSent
        ? 'User created. Invite email sent (link valid 12 hours).'
        : inviteError
          ? `User created, but invite email failed: ${inviteError}. Use Resend invite.`
          : 'User created. Invite email was not sent — use Resend invite.';

      navigate(ASSIGN_USERS_PATH, {
        replace: true,
        state: { flash },
      });
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Unable to save user.',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (entry) => {
    setStatus({ type: 'idle', message: '' });
    try {
      await updateWorkspaceUser(token, entry.id, { isActive: !entry.isActive });
      await loadUsers();
      setStatus({
        type: 'success',
        message: entry.isActive ? 'User deactivated.' : 'User activated.',
      });
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Unable to update user status.',
      });
    } finally {
      setPendingToggle(null);
    }
  };

  const handleResendInvite = async (entry) => {
    setStatus({ type: 'idle', message: '' });
    try {
      const payload = await resendWorkspaceUserInvite(token, entry.id);
      const sent = payload?.invite?.sent === true;
      setStatus({
        type: sent ? 'success' : 'error',
        message: sent
          ? `Invite resent to ${entry.email} (valid 12 hours).`
          : payload?.invite?.error || 'Invite could not be sent. Check SMTP configuration.',
      });
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Unable to resend invite.',
      });
    }
  };

  const confirmToggle = () => {
    if (pendingToggle) {
      handleToggleActive(pendingToggle);
    }
  };

  if (isForm) {
    if (loading || !formReady) {
      return (
        <div className="crm-panel px-5 py-8 text-sm text-[rgb(var(--crm-text-muted))]">
          Loading user form…
        </div>
      );
    }

    return (
      <form className="crm-panel grid gap-5 p-5" onSubmit={handleSubmit}>
        {status.type !== 'idle' && status.message ? (
          <StatusBanner status={status} setStatus={setStatus} />
        ) : null}

        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h4 className="text-base font-semibold">
              {mode === 'edit' ? 'Edit user' : 'Create user'}
            </h4>
            <p className="crm-text-muted mt-1 text-sm">
              {mode === 'create'
                ? 'Enter name and email — we will email an invite link (valid 12 hours) so they set their own password.'
                : 'Update login details and which sidebar sections they can open.'}
            </p>
          </div>
          <button
            type="button"
            className="crm-btn-secondary"
            onClick={() => navigate(ASSIGN_USERS_PATH)}
            disabled={saving}
          >
            Back to users
          </button>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="grid gap-1.5 text-sm">
            <span className="font-medium">Full name</span>
            <input
              className="crm-input"
              value={form.name}
              onChange={(event) =>
                setForm((current) => ({ ...current, name: event.target.value }))
              }
              required
              minLength={2}
              disabled={saving}
            />
          </label>
          <label className="grid gap-1.5 text-sm">
            <span className="font-medium">Login email</span>
            <input
              className="crm-input"
              type="email"
              value={form.email}
              onChange={(event) =>
                setForm((current) => ({ ...current, email: event.target.value }))
              }
              required
              disabled={saving}
            />
          </label>
          {mode === 'edit' ? (
            <label className="grid gap-1.5 text-sm">
              <span className="font-medium">New password (optional)</span>
              <input
                className="crm-input"
                type="password"
                value={form.password}
                onChange={(event) =>
                  setForm((current) => ({ ...current, password: event.target.value }))
                }
                minLength={8}
                placeholder="Leave blank to keep current"
                disabled={saving}
              />
            </label>
          ) : (
            <div className="grid gap-1.5 text-sm sm:col-span-2">
              <span className="font-medium">Invite email</span>
              <p className="crm-text-muted rounded-md border border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-page-bg))] px-3 py-2.5 text-sm">
                We will email an invite link (valid 12 hours). They set their own password before
                signing in — no password is sent in the email.
              </p>
            </div>
          )}
          <label className="grid gap-1.5 text-sm">
            <span className="font-medium">Phone (optional)</span>
            <input
              className="crm-input"
              value={form.phone}
              onChange={(event) =>
                setForm((current) => ({ ...current, phone: event.target.value }))
              }
              inputMode="numeric"
              maxLength={10}
              disabled={saving}
            />
          </label>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(event) =>
              setForm((current) => ({ ...current, isActive: event.target.checked }))
            }
            disabled={saving}
          />
          <span>Active (can sign in)</span>
        </label>

        <div className="grid gap-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h5 className="text-sm font-semibold">Sections</h5>
              <p className="crm-text-muted text-xs">{form.modules.length} selected</p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                className="crm-btn-secondary !min-h-8 !px-3 !text-xs"
                onClick={() =>
                  setForm((current) => ({ ...current, modules: [...availableModules] }))
                }
                disabled={saving}
              >
                Select all
              </button>
              <button
                type="button"
                className="crm-btn-secondary !min-h-8 !px-3 !text-xs"
                onClick={() => setForm((current) => ({ ...current, modules: [] }))}
                disabled={saving}
              >
                Clear
              </button>
            </div>
          </div>

          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {availableModules.map((moduleId) => {
              const checked = form.modules.includes(moduleId);
              return (
                <label
                  key={moduleId}
                  className={[
                    'flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2.5 text-sm transition',
                    checked
                      ? 'border-[rgb(var(--crm-accent))] bg-[rgb(var(--crm-accent-soft))]'
                      : 'border-[rgb(var(--crm-border))] hover:bg-[rgb(var(--crm-page-bg))]',
                  ].join(' ')}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleModule(moduleId)}
                    disabled={saving}
                  />
                  <span className="font-medium">{moduleLabel(moduleId)}</span>
                </label>
              );
            })}
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-2">
          <button
            type="button"
            className="crm-btn-secondary"
            onClick={() => navigate(ASSIGN_USERS_PATH)}
            disabled={saving}
          >
            Cancel
          </button>
          <button type="submit" className="crm-btn-primary" disabled={saving}>
            {saving ? 'Saving…' : mode === 'edit' ? 'Save changes' : 'Create user'}
          </button>
        </div>
      </form>
    );
  }

  return (
    <div className="grid gap-4">
      {status.type !== 'idle' && status.message ? (
        <StatusBanner status={status} setStatus={setStatus} />
      ) : null}

      <ConfirmDialog
        open={Boolean(pendingToggle)}
        title={pendingToggle?.isActive ? 'Deactivate user' : 'Activate user'}
        message={
          pendingToggle?.isActive
            ? `${pendingToggle.name} will not be able to sign in until you activate them again.`
            : `${pendingToggle?.name || 'This user'} will be able to sign in again with their assigned sections.`
        }
        confirmLabel={pendingToggle?.isActive ? 'Deactivate' : 'Activate'}
        cancelLabel="Cancel"
        onConfirm={confirmToggle}
        onCancel={() => setPendingToggle(null)}
      />

      <div className="grid gap-3 sm:grid-cols-3">
        <StatCard label="Total users" value={stats.total} />
        <StatCard label="Active" value={stats.active} tone="good" />
        <StatCard label="Inactive" value={stats.inactive} tone="muted" />
      </div>

      <div className="rounded-xl border border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-surface))] overflow-hidden shadow-xs">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-surface))] px-5 py-4">
          <div>
            <div className="flex items-center gap-2">
              <h4 className="text-base font-bold text-[rgb(var(--crm-text))]">All users</h4>
              <span className="rounded-full border border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-page-bg))] px-2 py-0.5 text-xs font-semibold text-[rgb(var(--crm-text-muted))]">
                {stats.total}
              </span>
            </div>
            <p className="mt-1 text-xs text-[rgb(var(--crm-text-muted))]">
              {stats.active} active · {stats.inactive} inactive · {stats.total} total
            </p>
          </div>
          <button
            type="button"
            className="flex items-center gap-1.5 rounded-lg bg-[rgb(var(--crm-accent))] px-3.5 py-2 text-xs font-semibold text-white shadow-xs transition hover:bg-[rgb(var(--crm-accent-hover))]"
            onClick={() => navigate(ASSIGN_USERS_NEW_PATH)}
          >
            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            <span>Create user</span>
          </button>
        </div>

        <div className="flex flex-wrap gap-2 border-b border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-page-bg))]/40 px-5 py-2.5">
          {[
            { id: 'all', label: 'All users' },
            { id: 'active', label: 'Active' },
            { id: 'inactive', label: 'Inactive' },
          ].map((option) => (
            <button
              key={option.id}
              type="button"
              className={[
                'rounded-lg px-3 py-1.5 text-xs font-semibold transition',
                statusFilter === option.id
                  ? 'bg-[rgb(var(--crm-surface))] text-[rgb(var(--crm-text))] shadow-xs border border-[rgb(var(--crm-border))]'
                  : 'text-[rgb(var(--crm-text-muted))] hover:bg-[rgb(var(--crm-surface)/0.7)] hover:text-[rgb(var(--crm-text))]',
              ].join(' ')}
              onClick={() => setStatusFilter(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>

        <UserTable
          users={filteredUsers}
          loading={loading}
          emptyLabel="No users match this filter."
          onEdit={(entry) => navigate(assignUserEditPath(entry.id))}
          onToggleActive={(entry) => setPendingToggle(entry)}
          onResendInvite={handleResendInvite}
        />
      </div>
    </div>
  );
}

export default AssignUsersPage;
